import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import api, { formatApiError } from "@/lib/api";
import { decideRequest } from "@/lib/approvals";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Check, X } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

const statusStyle = (s) => ({
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
}[s] || "bg-zinc-100 text-zinc-600");

export default function RegularizationsPage({ embedded = false }) {
  // Cancelling an approved regularization puts the attendance day back to what
  // it was before. Admin only, and always confirmed - it rewrites a day that
  // has already been signed off.
  const cancelReg = async (r) => {
    if (!window.confirm(`Cancel the approved regularization for ${r.employee_name || "this employee"} on ${r.date}?\n\nThe attendance for that day will be restored to its previous values.`)) return;
    const reason = window.prompt("Reason for cancelling (optional)") || undefined;
    try {
      const res = await api.post(`/attendance/regularize/${r.id}/cancel`, { reason });
      toast.success(`Cancelled · ${res.data?.attendance || "attendance updated"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const isMgr = user?.role !== "employee";
  const load = async () => {
    try { const { data } = await api.get("/attendance/regularize"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await decideRequest("regularization", id, decision === "approved"); toast.success(`Request ${decision}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6">
      {!embedded && (
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Regularizations</h1>
        <p className="mt-1 text-sm text-zinc-500">Attendance correction requests.</p>
      </div>
      )}
      <Card className="border-zinc-200 shadow-none">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                {isMgr && <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Employee</TableHead>}
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Date</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Requested In</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Requested Out</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Reason</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-zinc-500">Status</TableHead>
                {isMgr && <TableHead className="text-xs uppercase tracking-wider text-zinc-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50/60">
                  {isMgr && <TableCell className="text-sm font-medium">{r.employee?.name || "—"}</TableCell>}
                  <TableCell className="text-sm">{dayjs(r.date).format("D MMM YYYY")}</TableCell>
                  <TableCell className="text-sm tabular-nums">{r.requested_check_in || "—"}</TableCell>
                  <TableCell className="text-sm tabular-nums">{r.requested_check_out || "—"}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate text-zinc-600">{r.reason}</TableCell>
                  <TableCell><Badge variant="outline" className={`text-[11px] ${statusStyle(r.status)}`}>{r.status}</Badge></TableCell>
                  {isMgr && (
                    <TableCell className="text-right">
                      {r.status === "pending" ? (
                        <div className="inline-flex gap-2">
                          <Button size="sm" onClick={() => decide(r.id, "approved")} className="h-8 bg-emerald-600 hover:bg-emerald-700"><Check className="h-4 w-4" /></Button>
                          <Button size="sm" variant="outline" onClick={() => decide(r.id, "rejected")} className="h-8 border-zinc-300"><X className="h-4 w-4" /></Button>
                        </div>
                      ) : r.status === "approved" && user?.role === "admin" ? (
                      <Button size="sm" variant="outline" className="h-8 border-zinc-300 text-xs"
                        data-testid={`reg-cancel-${r.id}`}
                        onClick={() => cancelReg(r)}>Cancel</Button>
                    ) : <span className="text-xs text-zinc-400">—</span>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={isMgr ? 7 : 5} className="text-center text-sm text-zinc-500 py-8">No regularization requests.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}
